import { makeEntityScaffold } from '@classpass/saga-tools';
import { transformKeys } from 'helpers/object';
import camelCase from 'lodash-es/camelCase';
import snakeCase from 'lodash-es/snakeCase';

/**
 * Entity for retrieving applicable tax for one-off purchases such as top-up, bundles and class fees.
 *
 * @param currencyCode Three-letter representation of the currency, for instance 'USD'.
 * @param products Array of product tax information objects, for example:
 *
 * {
 *     // Required
 *     productType: 'BUNDLE',
 *     amount: 500, (in cents)
 *
 *     // Optional
 *     quantity: 2,
 *     taxableAmount: 4900, (in cents)
 *     description: '10-credit pack',
 *     statementDescriptor: 'credits',
 * }
 *
 * @returns
 * Populates the state with data of shape:
 * {
 *   currencyCode: <string>
 *   products: [
 *     {
 *       amount: <number> (in cents)
 *       taxableAmount: <number> (in cents)
 *       quantity: <number>
 *       productType: <string>
 *       totalTax: <number> (in cents)
 *     }
 *   ]
 * }
 *
 */
export const productSalesTaxEstimate = makeEntityScaffold(
  'SAGA.API.PRODUCT_SALES_TAX',
  (client, currencyCode, products) =>
    client.salesTax.product
      .estimate({
        currency: currencyCode,
        items: products.map(product =>
          transformKeys(snakeCase, {
            ...product,
            taxableAmount: product.taxableAmount ?? product.amount,
            quantity: product.quantity ?? 1,
          })
        ),
      })
      .then(({ currency, items }) => ({
        currencyCode: currency,
        products: items.map(product => {
          const { taxItems, ...productInfo } = transformKeys(camelCase, product);

          return {
            ...productInfo,
            totalTax: taxItems.reduce((total, taxItem) => total + taxItem.amount, 0),
          };
        }),
      }))
);
